// src/pages/vendors/purchase-order-form-components/ShippingAddressSection.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  alpha,
  useTheme
} from '@mui/material';
import {
  LocalShipping as ShippingIcon
} from '@mui/icons-material';

const ShippingAddressSection = ({ purchaseOrder, onPOChange, canEdit = true }) => {
  const theme = useTheme();

  return (
    <Paper sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
    }}>
      <Typography variant="h6" sx={{ 
        mb: 3, 
        fontWeight: 500, 
        fontSize: '1.2rem',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <ShippingIcon sx={{ fontSize: 28 }} />
        Shipping Address
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Street"
            fullWidth
            value={purchaseOrder.shipping_street || ''}
            onChange={(e) => onPOChange('shipping_street', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="City"
            fullWidth
            value={purchaseOrder.shipping_city || ''}
            onChange={(e) => onPOChange('shipping_city', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="State"
            fullWidth
            value={purchaseOrder.shipping_state || ''}
            onChange={(e) => onPOChange('shipping_state', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Zip"
            fullWidth
            value={purchaseOrder.shipping_zip || ''}
            onChange={(e) => onPOChange('shipping_zip', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Country"
            fullWidth
            value={purchaseOrder.shipping_country || ''}
            onChange={(e) => onPOChange('shipping_country', e.target.value)}
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ShippingAddressSection;